import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, CheckCircle, Loader2, FileText, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubsanarDocumento() {
  const { token } = useParams();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [exitoso, setExitoso] = useState(false);
  const [error, setError] = useState(null);

  const loadTokenInfo = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentos/info/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setTokenInfo(data);
      } else if (response.status === 404) {
        setError('Token inválido o no encontrado');
      } else if (response.status === 410) {
        setError('Este link ha expirado. Por favor contacta al equipo de soporte.');
      } else {
        setError('Error al cargar información');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTokenInfo();
  }, [loadTokenInfo]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error('El archivo es demasiado grande. Máximo 15MB');
        return;
      }
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Solo se permiten archivos PNG, JPG o PDF');
        return;
      }
      setArchivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!archivo) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('documento', archivo);

      const response = await fetch(
        `${BACKEND_URL}/api/documentos/subsanar/${token}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        setExitoso(true);
        toast.success('Documento recibido exitosamente');
      } else {
        const errorData = await response.json();
        toast.error(parseApiError(errorData.detail, 'Error al subir el documento'));
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error de conexión al subir el documento');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-yellow-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">Link No Válido</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <p className="text-gray-500 text-sm">
              Si necesitas ayuda, escríbenos a{' '}
              <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">
                desactivate@kompa.global
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (exitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          </div>

          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </div>

              <h1 className="text-3xl font-bold text-white mb-3">
                ¡Documento Recibido!
              </h1>
              
              <p className="text-gray-400 text-lg mb-8">
                Hemos recibido tu {tokenInfo?.documento_nombre} exitosamente
              </p>

              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-5 mb-8">
                <p className="text-green-300 text-sm">
                  ✓ Documento guardado correctamente<br />
                  ✓ Nuestro equipo lo revisará pronto<br />
                  ✓ Te contactaremos si necesitamos algo más
                </p>
              </div>

              <Button
                onClick={() => window.close()}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                Cerrar Ventana
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Documento Requerido</h1>
          <p className="text-gray-400">Expediente: {tokenInfo?.expediente_numero}</p>
        </div>

        {/* Info del documento solicitado */}
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Documento Solicitado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-300 text-lg font-semibold">
                {tokenInfo?.documento_nombre}
              </p>
              {tokenInfo?.motivo && (
                <div className="mt-3 pt-3 border-t border-blue-500/20">
                  <p className="text-gray-400 text-sm mb-1">Motivo de la solicitud:</p>
                  <p className="text-gray-300 text-sm">{tokenInfo.motivo}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Instrucciones */}
        <Card className="bg-orange-900/20 border-orange-500/30 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              Instrucciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-gray-300 text-sm space-y-2 list-disc list-inside">
              <li>Asegúrate de que el archivo sea <strong className="text-white">claro y legible</strong></li>
              <li>El documento debe estar <strong className="text-white">completo</strong> (sin cortes)</li>
              <li>La foto o escaneo debe estar <strong className="text-white">bien enfocado</strong></li>
              <li>Todos los datos deben ser <strong className="text-white">visibles</strong></li>
              <li>Formatos aceptados: PNG, JPG o PDF</li>
              <li>Tamaño máximo: 15MB</li>
            </ul>

            {tokenInfo?.usado && (
              <div className="mt-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-300 text-sm">
                  ℹ️ Ya enviaste un documento anteriormente. Si lo subes de nuevo, reemplazará el anterior.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Formulario de subida */}
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Subir Documento</CardTitle>
            <CardDescription className="text-gray-400">
              Archivo en formato PNG, JPG o PDF (máximo 15MB)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Input de archivo */}
              <div>
                <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-yellow-500/50 transition-colors">
                  <input
                    type="file"
                    id="documento-file"
                    className="hidden"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <label htmlFor="documento-file" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-300 font-medium mb-2">
                      {archivo ? archivo.name : 'Haz clic para seleccionar archivo'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      PNG, JPG o PDF - Máximo 15MB
                    </p>
                  </label>
                </div>
                {archivo && (
                  <p className="text-green-400 text-sm mt-2">✓ Archivo seleccionado: {archivo.name}</p>
                )}
              </div>

              {/* Info de expiración */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 text-sm">
                    <strong>Este link es válido por 7 días</strong>
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Si expira, contacta a nuestro equipo para recibir un nuevo link
                  </p>
                </div>
              </div>

              {/* Botón de submit */}
              <Button
                type="submit"
                disabled={uploading || !archivo}
                className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Enviar Documento
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          ¿Necesitas ayuda? Escríbenos a <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">desactivate@kompa.global</a>
        </p>
      </div>
    </div>
  );
}
